/**
 * View Component: Header
 * 紧凑版:logo + title 左;stats 收为 inline pills;RefreshButton + Heart 右
 *
 * Props:
 *   stats: { total, visible, favorites, books, withCover, hotspots, lastUpdated,
 *            newestDate, todayCount }
 *   onExport: (format: 'csv'|'json') => void   导出当前可见(默认 CSV)
 *   onShowToday: () => void  跳到最新一批(今日新增指示器点击)
 */
// T-Cron-2026-10-24 09:00 进度轮:把 <RefreshButton /> 从 .app 的直接 grid 子元素
// 挪进 header,必须显式 import —— 组件之间不共用 window 全局(除 main.jsx 挂载的几个)。
import RefreshButton from './RefreshButton.jsx';
// T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #11「PWA:安装到桌面」。
// 组件自己判断"能不能装"(beforeinstallprompt 有没有派发过),
// 不支持时**不渲染**,不给用户一个点了没反应的假按钮。
import InstallButton from './InstallButton.jsx';
// ⚠️ T-Cron-2026-10-25 20:00:这里**不要**写 `import React from 'react'`。
// vite.config.js 的 esbuild.jsxInject 会给每个 .jsx 注入 `import React from 'react'`,
// 显式再写一遍 ⇒ 构建报 `Identifier "React" has already been declared`
// ⇒ 整轮改动全部编译不过(这就是 T96 的坑,当时连续 3 轮生产贡献为 0)。
// 本文件用到的 React.* API 由 jsxInject 提供的全局 React 绑定满足。
// 2026-10-25 14:00 轮写的这行 import 让本文件变成了整个 src 树里**唯一**一个
// 显式 import React 的 .jsx ⇒ 本轮首件事就是把它删掉。

// 🔴 T-Cron-2026-11-03 14:00 午间 (T140) — 帮助入口按钮的**文案/图标**要跟
// **面板内容**一致(T138 改了面板,没改入口 ⇒ 承诺与内容不符)。
//
// 【缺陷(本轮运行时探针实测,pre-fix 4 处 FAIL)】
//   T138 把帮助面板在**纯触屏设备**上换成了 TOUCH_ACTIONS(10 条触屏操作),
//   面板标题也换成了「操作说明」,footer 也换成了「点右上角 ✕ 关闭」。
//   ⇒ 但 Header 上那个入口按钮**没换**:
//       aria-label = "查看键盘快捷键说明"
//       title      = "键盘快捷键说明（快捷键 ?）"
//       aria-keyshortcuts = "?"
//       图标       = ⌨ 键盘 glyph
//   ⇒ 触屏用户点进去看到的是**触屏操作清单**,按钮却写着「键盘快捷键说明」
//   ⇒ **点之前不知道点开是什么** = 入口失去意义。
//   ⇒ aria-keyshortcuts="?" 对触屏用户更荒谬:他们没有 ? 键。
//
// 【修法】
//   Header 接收 isTouchOnly ⇒ 入口的 aria-label / title / aria-keyshortcuts /
//   图标全部跟着变。桌面端**一字不改**(零回归)。
//
// 【为什么判据用 (hover:none) and (pointer:coarse) 而不是视口宽度】
//   与 App.jsx 的 isTouchOnly、T138 的面板切换、coverDownload.js 全部同源。
//   窄窗口的桌面浏览器仍然有物理键盘 ⇒ 按宽度切会让「桌面把窗口拉窄」
//   的用户凭空丢掉快捷键说明。
function Header({ stats, onExport, onShowToday, selectMode = false, onToggleSelectMode, viewedCount = 0, onShowHelp, onExportMenuOpenChange, isTouchOnly = false }) {
  // ── T-Cron-2026-10-25 14:00 — 导出格式菜单(CSV / JSON)────────────────
  //
  // 为什么这是真缺口而不是"锦上添花":
  //   harness/exporter.js 早就实现了 buildJSON() 和 exportPosts(posts,'json',...)
  //   —— **后端能力一直都在**。但 UI 只有一个"导出"按钮,写死 onExport('csv')。
  //   ⇒ JSON 导出对用户**完全不可达**。
  //   ⇒ 清单 #14 写着「✅ 完成」,但完成的是**模块**,不是**功能**。
  //   (与 T104 的 #7「拖拽排序」同一个陷阱:清单说完成 ≠ 线上可用。)
  //
  // 为什么用「split button + 弹出菜单」而不是两个并排按钮:
  //   · header-right 在移动端已经很挤(720px 以下连"导出"文字都隐藏了);
  //     再加一个"JSON"按钮会把 375px 宽的手机 header 挤爆。
  //   · CSV 是高频默认(Excel 直接开),JSON 是低频(喂工具/存档),
  //     所以主按钮保持 CSV(一点即出),旁边挂一个 ▾ 露出 JSON。
  //
  // a11y 契约:
  //   · 菜单是 listbox 语义:role="menu" + role="menuitem";
  //   · 打开时焦点移到第一个菜单项,Esc 关闭并把焦点还给 ▾ 按钮;
  //   · 点外部关闭;role="menu" 上 aria-label 说明用途。
  //   · 键盘:↑/↓ 在菜单项间移动,Enter/Space 触发,Esc 关闭。
  const [exportMenuOpen, setExportMenuOpen] = React.useState(false);
  const exportMenuRef = React.useRef(null);
  const exportToggleRef = React.useRef(null);
  const exportMenuItemRefs = React.useRef([]);

  const closeExportMenu = React.useCallback((returnFocus) => {
    setExportMenuOpen(false);
    if (returnFocus && exportToggleRef.current) {
      exportToggleRef.current.focus();
    }
  }, []);

  // 点击外部关闭 + Esc 关闭
  React.useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        closeExportMenu(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeExportMenu(true);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [exportMenuOpen, closeExportMenu]);

  // 打开时把焦点移到第一个菜单项
  React.useEffect(() => {
    if (exportMenuOpen && exportMenuItemRefs.current[0]) {
      exportMenuItemRefs.current[0].focus();
    }
  }, [exportMenuOpen]);

  // 🔴 T138 (2026-11-03 03:30 早间) — 把「导出菜单开着」这个状态**上报**给 App。
  //
  // 【为什么必须上报】导出菜单是 role="menu"(不是 aria-modal),
  //   所以它不在 App 的模态栈里 ⇒ 菜单开着时按单键会**穿透**到背景:
  //     · 按 `2` → 背景看板被切成列表视图(实测 pre-fix:before='post-grid '
  //            after='post-grid list-mode')
  //     · 按 `e` → **导出两次**(菜单里的 item 一次 + 全局 e 一次,
  //            实测 pre-fix:downloads=2)—— 用户看到两个下载框
  //     · 按 `m` → 背景进入多选态(实测 pre-fix:batch_bar=True)
  //   与 T136「详情抽屉按 m 穿透」/ T137「筛选抽屉按 m 穿透」同族。
  //
  // 【为什么用回调而不是把状态提到 App】菜单的 open/close 有 4 个触发点
  //   (▾ 按钮点击 / 点外部关闭 / Esc 关闭 / 选中菜单项后关闭),
  //   全部收口在 setExportMenuOpen ⇒ 在**这一处**用 effect 上报,
  //   比在 4 个调用点各写一遍 onExportMenuOpenChange 可靠
  //   (漏一个 ⇒ App 里的状态与真实状态不一致 ⇒ 守卫出现随机空洞)。
  //
  // 【为什么用 effect 而不是直接在 setExportMenuOpen 处回调】state 更新是异步的,
  //   而 App 需要的正是「当前真实值」⇒ effect 天然在 DOM 提交后触发。
  React.useEffect(() => {
    if (typeof onExportMenuOpenChange === 'function') {
      onExportMenuOpenChange(exportMenuOpen);
    }
  }, [exportMenuOpen, onExportMenuOpenChange]);

  const doExport = React.useCallback((format) => {
    // ⚠️ 触感反馈只在**这里**触发一次。
    // App.jsx 的 exportVisible() 在导出成功后也会 buzz(12) —— 那是"成功"震动。
    // 本文件不再重复 buzz,否则一次导出会震两下(10ms + 12ms 连着来,
    // 在低端机上听起来像"卡了一下")。
    if (onExport) onExport(format);
  }, [onExport]);

  // 菜单内 ↑/↓ 导航 + Enter/Space 触发
  const onExportMenuKeyDown = (e) => {
    // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
    //
    // 【症状】按住 Enter 不放 ⇒ 反复 document.activeElement.click()
    //   ⇒ 连续导出 5~10 次 CSV/JSON(浏览器弹一串下载确认);
    //   按住 ↓ 不放 ⇒ 菜单项焦点高速滚动。
    if (e.repeat && !(e.ctrlKey || e.metaKey || e.altKey)) return;
    const items = exportMenuItemRefs.current.filter(Boolean);
    if (!items.length) return;
    const idx = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(idx + 1) % items.length].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length].focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      document.activeElement.click();
    }
  };

  const fmtLast = () => {
    const t = stats.lastUpdated;
    if (!t || t === '—') return '—';
    // 2026-09-12 -> 09-12 (短)
    const parts = t.split('-');
    return parts.length === 3 ? `${parts[1]}-${parts[2]}` : t;
  };

  // T-Cron-2026-10-23 08:00 — 改进清单 #9「统计:今日新增指示」。
  // 「今天新增 N 篇」做成一个可点的胶囊:点一下就跳到最新一批。
  // 口径:数据集里 date 最大的一天(与 ContentService.isToday 同判据),
  // 不是系统日期 —— 跑批是 4 次/天,系统"今天"可能还没有数据。
  const fmtNewest = (d) => {
    if (!d) return '';
    const parts = String(d).split('-');
    return parts.length === 3 ? `${parts[1]}-${parts[2]}` : d;
  };
  const showToday = !!stats.todayCount && !!stats.newestDate;

  return (
    <header className="app-header">
      <div className="header-left">
        <div className="logo-mark">选</div>
        <div className="header-title-block">
          {/* T-Cron-2026-10-25 20:00 — 改进清单 #10「无障碍:标题层级」。
             全站此前**一个标题元素都没有**(只有一个 <h2> 在 PostDetail 抽屉里)
             ⇒ 读屏用户既没有「页面标题」也没有「按标题跳转」。
             把应用名升级成 <h1> 是标题层级的根:
               h1 = 内容选材看板(页面级标题)
               h2 = 每个日期分组(ContentGrid)
             ⚠️ 视觉零回归:.header-title 的字号/字重/字距都在 app.css 里
                显式写死,但 h1 默认有 margin ⇒ reset 写在 a11y-headings.css。 */}
          <h1 className="header-title">内容选材看板</h1>
          <div className="header-subtitle">
            <span className="header-tag">小红书</span>
            <span className="header-dot">·</span>
            <span><strong>{stats.total}</strong> 篇</span>
            <span className="header-dot">·</span>
            <span>更新 {fmtLast()}</span>
          </div>
        </div>
      </div>
      <div className="header-right">
        <div className="header-pills">
          <span className="header-pill" title="有封面">
            <span className="header-pill-num" style={{ color: 'var(--text-accent)' }}>{stats.withCover}</span>
            <span className="header-pill-label">封面</span>
          </span>
          <span className="header-pill-divider"></span>
          <span className="header-pill" title="本书籍">
            <span className="header-pill-num">{stats.books}</span>
            <span className="header-pill-label">书籍</span>
          </span>
          <span className="header-pill-divider"></span>
          <span className="header-pill" title="个热点">
            <span className="header-pill-num">{stats.hotspots}</span>
            <span className="header-pill-label">热点</span>
          </span>
          <span className="header-pill-divider"></span>
          <span className="header-pill" title="当前显示">
            <span className="header-pill-num">{stats.visible}</span>
            <span className="header-pill-label">显示</span>
          </span>
        </div>
        {/* T-Cron-2026-10-23 08:00 — 改进清单 #9「今日新增指示」。
            之前 header 只显示总量/封面/书籍/热点/显示,用户**看不出今天新来了多少**,
            每次打开都要自己往下翻到第一个日期分组才能发现"今天又多了 12 篇"。
            现在做成一个可点的胶囊:点一下直接筛到最新一批。
            口径:数据集里 date 最大的一天(不是系统日期)。 */}
        {showToday && (
          <button
            className="header-today-pill"
            onClick={onShowToday}
            aria-label={`跳到最新一批：${fmtNewest(stats.newestDate)} 新增 ${stats.todayCount} 篇`}
            title={`${stats.newestDate} 新增 ${stats.todayCount} 篇，点击只看这一批`}
          >
            <span className="header-today-dot" aria-hidden="true"></span>
            <span className="header-today-text">最新 {fmtNewest(stats.newestDate)}</span>
            <span className="header-today-count">+{stats.todayCount}</span>
          </button>
        )}
        {/* T-Cron-2026-10-24 03:30 早间:「多选」模式开关。
            改进清单 #8 的批量复制/收藏原本只能靠**长按**进入 ——
            桌面端没有 touch 事件 ⇒ 这个功能在桌面完全不可用。
            这个按钮 + 快捷键 M 补上桌面入口;移动端长按仍然更顺手。 */}
        {/* 🔴 T144(2026-11-04 08:00)—— 把「操作按钮组」包成一个整体 .header-actions。
            背景:T143 下轮建议第 4 条「.manual-banner 与 header 第二行纵向相交」。
            实测量到:移动端 header 固定 52px 高,但内容换行到 120~136px
            ⇒ 溢出 68~84px ⇒ 与 banner 相交 56~70px ⇒ banner 标题被 header-right 盖住
            (3755~4472 px² 重叠面积)。
            修法第一步:让 header 高度自适应(min-height 代替 height)⇒ 相交已消除。
            本条是第二步:**把散落的 7 个操作按钮聚成一组**,
            让它们**整体换行**而不是逐个换行 ⇒ header 从 3 行降到 2 行
            ⇒ 移动端首屏多回收约 50px。
            ⚠️ 纯包裹,**不改任何交互/状态/可访问性属性**。
            ⚠️ 桌面端 .header-actions{display:contents} ⇒ DOM 层级对布局零影响(零回归)。 */}
        <div className="header-actions">
        <button
          className={`header-select-btn ${selectMode ? 'active' : ''}`}
          onClick={onToggleSelectMode}
          aria-pressed={!!selectMode}
          aria-label={selectMode ? '退出多选模式' : '进入多选模式，可批量复制或收藏'}
          /* T-Cron-2026-10-31 02:00 (T127) — 改进清单 #4「快捷键」。
             aria-keyshortcuts 让读屏用户能听到「这个按钮有快捷键 M」,
             与 title 里已经写明的契约一致 —— 之前只有视觉层(title)
             知道 M 存在,辅助技术完全不知道。
             ⚠️ 用大写 M:WAI-ARIA 规定单字母快捷键应大写,
             且「Shift+M」才是真正的区分方式。 */
          /* 🔴 T141(2026-11-03 20:00 晚间轮)—— 触屏端不承诺用不了的操作。
             触屏没有物理键盘 ⇒ title 里的「快捷键 M」是**永远点不出来的承诺**,
             且 title tooltip 在触屏上根本不显示 ⇒ 这段文案对触屏用户是纯噪音。
             aria-keyshortcuts="M" 同理:读屏会播报「有快捷键 M」,但按不出来。
             ⇒ 触屏分支拿掉快捷键承诺,改成描述**这个按钮实际会做什么**。
             桌面端**一字不改**(零回归)。与 T140 的帮助入口修法同源
             (同一个 isTouchOnly 通道,见 Header.jsx 顶部注释)。 */
          aria-keyshortcuts={isTouchOnly ? undefined : 'M'}
          title={selectMode
            ? (isTouchOnly ? '退出多选模式' : '退出多选（快捷键 M）')
            : (isTouchOnly ? '批量复制或收藏已选素材' : '多选：批量复制 / 收藏（快捷键 M）')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 11l3 3L22 4"></path>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
          </svg>
          <span className="header-select-label">多选</span>
        </button>
        {/* T-Cron-2026-10-23 03:30:导出当前可见(CSV)。
            上一轮只有「已选素材」能导出(要一篇一篇点收藏),
            日常场景「筛出今天这批 → 直接导出」没有入口。
            这个按钮导出的口径 = 当前筛选后可见的集合,不是全量。

            T-Cron-2026-10-25 14:00:改成 split button(主按钮 CSV + ▾ 菜单露 JSON)。
            原因:harness/exporter.js 早就实现了 buildJSON(),
            但 UI 写死 onExport('csv') ⇒ **JSON 导出对用户完全不可达**
            (清单 #14 标着「✅ 完成」,完成的只是模块,不是功能)。
            主按钮保持 CSV(高频,一点即出),▾ 里放 JSON / CSV 两个显式选项。 */}
        <div className="header-export-wrap" ref={exportMenuRef}>
          <button
            className="header-export-btn"
            onClick={() => doExport('csv')}
            aria-label={`导出当前可见的 ${stats.visible} 篇为 CSV`}
            /* T-Cron-2026-10-31 02:00 (T127) — 改进清单 #4「快捷键」。
               与「多选」按钮同理:快捷键 E 之前只写在 title 里,
               读屏用户听不到。aria-keyshortcuts="E" 把它暴露给辅助技术。 */
            /* 🔴 T141(2026-11-03 20:00 晚间轮)—— 同「多选」按钮。
               触屏上 title tooltip 永不显示 ⇒ 这段文案触屏用户看不到;
               aria-keyshortcuts="E" 对无物理键盘的用户是**无效契约**
               (读屏播报「有快捷键 E」,但按不出来)。
               ⇒ 触屏分支拿掉快捷键承诺,桌面端一字不改。 */
            aria-keyshortcuts={isTouchOnly ? undefined : 'E'}
            title={isTouchOnly
              ? `导出当前可见的 ${stats.visible} 篇为 CSV`
              : '导出当前可见为 CSV（快捷键 E）'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            <span className="header-export-label">导出</span>
          </button>
          <button
            ref={exportToggleRef}
            className="header-export-toggle"
            onClick={() => setExportMenuOpen(v => !v)}
            aria-haspopup="menu"
            aria-expanded={exportMenuOpen}
            /* 🔴 T141(2026-11-03 20:00 晚间轮)—— ▾ 按钮在触屏上**完全不可发现**。
               实测(390/320 触屏):它只有一个 10px 的 chevron SVG,
               没有可见文字,触控区只有 24×30(a11y.css 的 .header-help-btn
               早已加固到 40×40,这个按钮漏了)。
               ⇒ 触屏用户看到的是「一个下载图标 + 一条竖线 + 一个 chevron」,
                 完全判断不出这个 chevron 管的是「导出格式」。
               ⇒ 触屏分支:aria-label 改成动作描述 + 追加可见文字「格式」。
                 桌面端一字不改(仍保持纯图标)。 */
            aria-label={isTouchOnly ? '展开导出格式菜单' : '选择导出格式'}
            title={isTouchOnly ? '展开导出格式菜单（CSV / JSON）' : '导出格式'}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
            {/* 🔴 T141:触屏补可见文字(桌面由 CSS 隐藏)—— 让用户知道这个 chevron 管「格式」 */}
            <span className="header-export-toggle-label" aria-hidden="true">格式</span>
          </button>
          {exportMenuOpen && (
            <div
              className="header-export-menu"
              role="menu"
              aria-label="导出格式"
              onKeyDown={onExportMenuKeyDown}
            >
              <button
                ref={(el) => { exportMenuItemRefs.current[0] = el; }}
                role="menuitem"
                className="header-export-menu-item"
                onClick={() => { closeExportMenu(true); doExport('csv'); }}
              >
                <span className="header-export-menu-title">CSV</span>
                <span className="header-export-menu-desc">一行一个版本 · Excel / 飞书表格</span>
              </button>
              <button
                ref={(el) => { exportMenuItemRefs.current[1] = el; }}
                role="menuitem"
                className="header-export-menu-item"
                onClick={() => { closeExportMenu(true); doExport('json'); }}
              >
                <span className="header-export-menu-title">JSON</span>
                <span className="header-export-menu-desc">一个对象一篇 · 完整版本结构</span>
              </button>
            </div>
          )}
        </div>
        <div className="fav-count-pill" title="已选">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true" focusable="false">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
          <span>{stats.favorites}</span>
        </div>
        {/* 🔴 T135(2026-11-02 20:00):快捷键说明的**可见入口**。
            *
            * 【为什么这是真缺口(可达性缺口,不是锦上添花)】
            *   App.jsx 里有一份 20 条的 SHORTCUT_GROUPS 帮助面板,
            *   但**唯一的打开方式是键盘 `?`**。
            *   ⇒ 触屏用户(手机 = 本项目的主要使用场景)永远打不开它;
            *   ⇒ 桌面用户也得先「知道」按 ? 才可能发现。
            *   代码自己的注释写着「没有说明书 = 快捷键等于不存在」——
            *   对移动端来说,说明书本身都打不开。
            *
            * 【为什么不能靠 title/aria-label 代替按钮】
            *   那两个只在**已经看到该按钮**时才生效 ⇒ 依然是零入口。
            *
            * 修法:header 右侧放一个 ⌨ 按钮,aria-keyshortcuts="?" 暴露
            * 快捷键契约,触屏/鼠标/读屏三条路径都能打开面板。 */}
        {onShowHelp && (
          <button
            className="header-help-btn"
            onClick={onShowHelp}
            // 🔴 T140:入口文案必须跟**面板内容**一致。
            //   触屏 ⇒ 面板是「操作说明」(10 条触屏操作)⇒ 入口也这么说,
            //   而且**不暴露** aria-keyshortcuts="?"(触屏没有 ? 键)。
            //   桌面 ⇒ 一字不改(零回归)。
            aria-label={isTouchOnly ? '查看操作说明' : '查看键盘快捷键说明'}
            aria-keyshortcuts={isTouchOnly ? undefined : '?'}
            title={isTouchOnly ? '操作说明（怎么看、怎么用）' : '键盘快捷键说明（快捷键 ?）'}
          >
            {/* 🔴 T140:图标也跟着变 —— 触屏用户看到 ⌨ 会以为要找键盘。 */}
            {isTouchOnly ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <circle cx="12" cy="12" r="9"></circle>
                <path d="M9.2 9a2.8 2.8 0 0 1 5.5.7c0 1.9-2.7 2.4-2.7 4"></path>
                <path d="M12 17h.01"></path>
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect>
                <path d="M6 9h.01M10 9h.01M14 9h.01M18 9h.01M7 14h10"></path>
              </svg>
            )}
          </button>
        )}
        {/* T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #9「统计:view count」。
            「已读」胶囊:累计打开过详情多少篇。
            口径 = 本地 localStorage 里记录过的浏览次数(只在打开详情时 +1),
            不上报服务器 —— 这是使用者个人的复盘信号,不是公开榜单。
            只在 >0 时渲染,避免 header 常驻一个恒为 0 的占位。 */}
        {viewedCount > 0 && (
          <div className="view-count-pill" title="已读过的篇数（本地记录）">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            <span className="view-count-num">{viewedCount}</span>
          </div>
        )}
        {/* T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #11「PWA:安装到桌面」。
            InstallButton 自己判断能不能装:不支持 / 已装成 standalone 时不渲染。 */}
        <InstallButton />
        {/* T-Cron-2026-10-24 08:00 — P0 布局修复:刷新按钮归位到 header 右侧。
            之前 <RefreshButton /> 是 <div className="app"> 的**直接 grid 子元素**,
            而 .app 是 `grid-template-columns:280px 1fr` 的两列网格 ⇒
            刷新按钮占了第 1 个网格单元(第 1 行第 1 列),
            于是:
              · .app-sidebar 被挤到第 2 列(1000px 宽的那一列);
              · .app-main  被挤到第 3 个自动单元(只剩 280px 宽)。
            后果(线上实测):侧栏变成 1000px 宽的一条横带、滚动条高达 15715px,
            正文区被压成 280px ⇒ 「小红书运营操作手册」被挤成**一个字一行**的竖排,
            卡片网格只剩 226px 宽(桌面端应该 4-5 列)。
            修法:把按钮放进 .header-right(它本来就是 header 的一部分),
            .app 的直接子元素回到 3 个(header / sidebar / main),网格恢复正常。 */}
        <RefreshButton />
        </div>
      </div>
    </header>
  );
}

window.Header = Header;

export default Header;
